import Header from '@/components/Header';
import Footer from '@/components/Footer';
import CustomCursor from '@/components/CustomCursor';
import { Toaster } from 'sonner';
import { CheckCircle, Sparkles, Shield, Clock, Users, FileText, Brain, Target, Layers, BarChart3 } from 'lucide-react';

const ProductPage = () => {
  const features = [
    {
      icon: Brain,
      title: 'AI Generator',
      description: 'Drafts RFI / RFQ / RFP / SOW (sections, factors, evaluation plan) from your inputs with human review and full traceability.',
      color: 'from-purple-500 to-indigo-600'
    },
    {
      icon: Shield,
      title: 'FAR/DFARS-aware Drafting',
      description: 'Flags clause conflicts and compliance gaps with explainable rationale. Keeps every recommendation audit-ready.',
      color: 'from-blue-500 to-cyan-600'
    },
    {
      icon: FileText,
      title: 'Automated Submission Portal',
      description: 'Structured submissions + completeness checks + audit trail. Supports full RFP proposals: technical, price, past performance.',
      color: 'from-emerald-500 to-teal-600'
    },
    {
      icon: Target,
      title: 'Evaluation & Source Selection',
      description: 'AI scoring that\'s traceable back to documents and data. Every score links to the evidence that generated it.',
      color: 'from-orange-500 to-red-600'
    },
    {
      icon: Layers,
      title: 'Diligence Modules',
      description: 'Legal, Budget, HR, Tech/Sec, and Integration modules that run in parallel, each with its own checklists and risk indicators.',
      color: 'from-pink-500 to-rose-600'
    },
    {
      icon: BarChart3,
      title: 'Portfolio Dashboard',
      description: 'Live view of pipeline, cycle times, vendor performance, and funding risk across all active acquisitions.',
      color: 'from-violet-500 to-purple-600'
    }
  ];

  const capabilities = [
    'AI-powered document generation with human oversight',
    'Configurable workflows that adapt to your agency',
    'Real-time collaboration across teams',
    'Secure connectors for existing systems of record',
    'Traffic-light risk indicators at every stage',
    'Plain-language summaries for leadership briefings'
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-indigo-50">
      <CustomCursor />
      <Toaster position="top-center" />
      <Header />
      
      <main>
        {/* Hero Section */}
        <section className="relative pt-20 pb-24 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-indigo-100/50 via-transparent to-purple-100/50"></div>
          <div className="max-w-7xl mx-auto px-6 lg:px-8 relative">
            <div className="text-center max-w-4xl mx-auto">
              <span className="inline-block px-4 py-1.5 bg-indigo-100 text-indigo-700 text-sm font-semibold rounded-full mb-6">
                Product Overview
              </span>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight">
                The Acquisition Command Center
                <span className="block text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-blue-600">
                  Your Team Deserves
                </span>
              </h1>
              <p className="text-lg sm:text-xl text-gray-600 mb-8 leading-relaxed">
                AcuSightPro combines AI-powered drafting, structured evaluation, and end-to-end workflow management into one platform built specifically for government acquisition professionals.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a
                  href="/contact"
                  className="px-8 py-3 bg-gradient-to-r from-purple-600 to-blue-600 text-white font-semibold rounded-full hover:shadow-lg hover:shadow-purple-500/30 transition-all hover:-translate-y-0.5"
                >
                  Request a Demo
                </a>
                <a
                  href="/acquisition-portal"
                  className="px-8 py-3 bg-white border-2 border-gray-200 text-gray-700 font-semibold rounded-full hover:border-purple-300 hover:text-purple-700 transition-all"
                >
                  View Acquisition Portal
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* ACUMEN-10 AI Engine Section */}
        <section className="py-16 bg-gradient-to-br from-gray-900 via-indigo-900 to-purple-900 text-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-2xl font-bold shadow-lg">
                    A7
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold">ACUMEN-10</h2>
                    <p className="text-indigo-300">Powered by ACI, Adaptive Compound Intelligence</p>
                  </div>
                </div>
                <p className="text-lg text-indigo-200 mb-6 leading-relaxed">
                  A purpose-built AI engine optimized for government acquisition workflows. ACUMEN-10 features automatic 
                  error correction, continuous learning from user feedback, and multi-tier failover architecture for 
                  99.97% guaranteed uptime.
                </p>
                <div className="grid grid-cols-2 gap-4 mb-6">
                  <div className="bg-white/10 rounded-xl p-4 backdrop-blur-sm">
                    <div className="text-2xl font-bold text-white">99.97%</div>
                    <div className="text-sm text-indigo-300">Uptime SLA</div>
                  </div>
                  <div className="bg-white/10 rounded-xl p-4 backdrop-blur-sm">
                    <div className="text-2xl font-bold text-white">~1.2s</div>
                    <div className="text-sm text-indigo-300">Avg Response</div>
                  </div>
                  <div className="bg-white/10 rounded-xl p-4 backdrop-blur-sm">
                    <div className="text-2xl font-bold text-white">4-Tier</div>
                    <div className="text-sm text-indigo-300">Failover System</div>
                  </div>
                  <div className="bg-white/10 rounded-xl p-4 backdrop-blur-sm">
                    <div className="text-2xl font-bold text-white">Self-Healing</div>
                    <div className="text-sm text-indigo-300">Auto-Recovery</div>
                  </div>
                </div>
                <a
                  href="/tech-security"
                  className="inline-flex items-center gap-2 text-indigo-300 hover:text-white transition-colors"
                >
                  Learn more about AI Reliability & Security
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </a>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20">
                <h3 className="font-bold text-white mb-4">AI-Powered Features</h3>
                <ul className="space-y-3">
                  {[
                    'Document Generation (RFI, RFP, RFQ, SOW, PWS)',
                    'Contract Analysis & Risk Detection',
                    'IGCE Generation with Labor Categories',
                    'FAR/DFARS Compliance Checking',
                    'Market Research & Vendor Discovery',
                    'Team Insights & Capacity Forecasting',
                    'Voice-Enabled AI Assistant'
                  ].map((feature, i) => (
                    <li key={i} className="flex items-center gap-3 text-indigo-200">
                      <svg className="w-5 h-5 text-emerald-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Features Grid */}
        <section className="py-20">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
                Everything You Need to Accelerate Acquisitions
              </h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                From initial requirements to award, AcuSightPro provides the tools your team needs to work faster and smarter.
              </p>
            </div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {features.map((feature, index) => (
                <div
                  key={index}
                  className="group bg-white rounded-2xl p-8 shadow-sm border border-gray-100 hover:shadow-xl hover:border-purple-200 transition-all duration-300"
                  data-robot-tooltip={feature.description}
                >
                  <div className={`inline-flex p-3 rounded-xl bg-gradient-to-br ${feature.color} mb-6`}>
                    <feature.icon className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-purple-700 transition-colors">
                    {feature.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    {feature.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* AI Relevance Ranking Callout */}
        <section className="py-20 bg-gradient-to-br from-purple-50 to-indigo-50">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="grid lg:grid-cols-[1.1fr_1fr] gap-12 items-center">
              <div>
                <span className="inline-block px-4 py-1.5 bg-purple-100 text-purple-700 text-sm font-semibold rounded-full mb-4">
                  AI Relevance Ranking
                </span>
                <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-5 leading-tight">
                  Every Result Comes With a Score.{" "}
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-indigo-600">
                    And the Reason.
                  </span>
                </h2>
                <p className="text-lg text-gray-700 mb-5 leading-relaxed">
                  When AcuSightPro pulls market research, sources sought responses, capability statements, or past performance, every result is ranked by AI relevance with a numeric score and a transparent rationale.
                </p>
                <p className="text-gray-600 mb-6 leading-relaxed">
                  Best matches surface first. Every score links back to the inputs that drove it: NAICS match, keyword density, set-aside alignment, geographic relevance, recency, and historical award patterns. Default sort is by AI relevance, high to low. Contracting officers can switch to recency or deadline at any time.
                </p>
                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-emerald-100 text-emerald-700 text-xs font-bold flex-shrink-0">1</span>
                    <span className="text-gray-700"><strong className="text-gray-900">Sourced.</strong> Every score traces back to the documents and data fields that produced it.</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-emerald-100 text-emerald-700 text-xs font-bold flex-shrink-0">2</span>
                    <span className="text-gray-700"><strong className="text-gray-900">Explainable.</strong> Hover any score and read the rationale in plain language.</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-emerald-100 text-emerald-700 text-xs font-bold flex-shrink-0">3</span>
                    <span className="text-gray-700"><strong className="text-gray-900">Defensible.</strong> Audit-ready. Holds up to oversight and protest review.</span>
                  </div>
                </div>
              </div>

              {/* Visual mock of ranked list */}
              <div className="bg-white border border-gray-200 rounded-2xl shadow-xl p-5">
                <div className="flex items-center justify-between mb-3 pb-3 border-b border-gray-100">
                  <div className="flex items-center gap-2">
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide bg-purple-100 text-purple-800 border border-purple-200">
                      <Sparkles className="w-3 h-3" />
                      Ranked by AI Relevance
                    </span>
                  </div>
                  <div className="text-xs text-gray-500">Sort: AI Relevance ↓</div>
                </div>
                <div className="space-y-2.5">
                  {[
                    { title: 'Sources Sought: Specialty Care Telehealth', set: 'SDVOSB', score: 94, tone: 'emerald' },
                    { title: 'Capability Statement: Veteran Telehealth Integrator', set: 'SDVOSB', score: 89, tone: 'emerald' },
                    { title: 'RFI Response: Async Specialty Consult Workflow', set: 'WOSB', score: 81, tone: 'blue' },
                    { title: 'Market Research Memo: Hub-and-Spoke FY25', set: 'N/A', score: 74, tone: 'blue' },
                    { title: 'GSA Schedule Pricing: IT Professional Services', set: 'N/A', score: 68, tone: 'amber' }
                  ].map((row) => (
                    <div key={row.title} className="flex items-center gap-3 p-2.5 rounded-lg hover:bg-gray-50 transition-colors">
                      <span className="text-[10px] font-bold px-1.5 py-0.5 rounded text-white bg-gray-700 flex-shrink-0">{row.set}</span>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-gray-900 truncate">{row.title}</div>
                      </div>
                      <span
                        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-bold border flex-shrink-0 ${
                          row.tone === 'emerald'
                            ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                            : row.tone === 'blue'
                              ? 'bg-blue-50 text-blue-700 border-blue-200'
                              : 'bg-amber-50 text-amber-700 border-amber-200'
                        }`}
                      >
                        <Sparkles className="w-3 h-3" />
                        {row.score}%
                      </span>
                    </div>
                  ))}
                </div>
                <div className="mt-3 pt-3 border-t border-gray-100 text-xs text-gray-500 italic">
                  Sample. Hover any score in the live app for the rationale.
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Capabilities Section */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="max-w-3xl mx-auto text-center">
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
                Built for Real Acquisition Work
              </h2>
              <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                AcuSightPro was designed alongside acquisition professionals who were tired of scattered documents, inconsistent reviews, and slow cycles. Every feature maps directly to how your team actually works.
              </p>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mt-8">
              {capabilities.map((capability, index) => (
                <div key={index} className="flex items-start gap-3 bg-white p-4 rounded-xl shadow-sm border border-gray-100">
                  <CheckCircle className="w-6 h-6 text-emerald-500 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">{capability}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20">
          <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-purple-100 rounded-full text-purple-700 font-medium mb-6">
              <Sparkles className="w-5 h-5" />
              <span>Ready to transform your acquisition process?</span>
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
              See AcuSightPro in Action
            </h2>
            <p className="text-lg text-gray-600 mb-8">
              Schedule a personalized demo with our team and discover how AcuSightPro can help your organization do more acquisitions with the team you already have.
            </p>
            <a
              href="/contact"
              className="inline-flex px-8 py-4 bg-gradient-to-r from-purple-600 to-blue-600 text-white font-semibold rounded-full hover:shadow-lg hover:shadow-purple-500/30 transition-all hover:-translate-y-0.5"
            >
              Contact Us Today
            </a>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default ProductPage;
